import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Flame, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";
import { listGamesCached, prefetchGameEmbed } from "@/lib/api";

const highlight = (name, q) => {
  if (!q) return name;
  const idx = name.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return name;
  return (
    <>
      {name.slice(0, idx)}
      <span className="bg-[#ffe4d1] text-[#e85d00] rounded px-0.5">
        {name.slice(idx, idx + q.length)}
      </span>
      {name.slice(idx + q.length)}
    </>
  );
};

// Memoised tile: re-renders only when name/slug/query changes for its row.
const GameTile = memo(function GameTile({ slug, name, query }) {
  const onPrefetch = () => prefetchGameEmbed(slug);
  return (
    <Link
      to={`/game/${slug}`}
      data-testid={`game-tile-${slug}`}
      onMouseEnter={onPrefetch}
      onFocus={onPrefetch}
      onTouchStart={onPrefetch}
      className="tile group bg-white border border-[#efe3d6] rounded-2xl p-4 sm:p-5 flex flex-col justify-between min-h-[112px] sm:min-h-[128px]"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-semibold tracking-widest uppercase text-[#5c534d]">
          Play
        </span>
        <ArrowUpRight
          size={18}
          strokeWidth={2.2}
          className="tile-arrow text-[#5c534d]"
        />
      </div>
      <h3 className="tile-title font-display font-semibold text-base sm:text-lg leading-tight mt-6">
        {highlight(name, query)}
      </h3>
    </Link>
  );
});

export default function GamesPage() {
  const [games, setGames] = useState([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const data = await listGamesCached();
        setGames(data);
      } catch (e) {
        console.error(e);
        toast.error("Could not load games. Try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 120ms debounce so filtering the imported library happens after typing pauses.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 120);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return games;
    return games.filter((g) => {
      if (g.name.toLowerCase().includes(q)) return true;
      if ((g.keywords || []).some((k) => k.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [games, debouncedQuery]);

  return (
    <div className="min-h-screen bg-[#fff7f0] text-[#1a1613]">
      <NavBar
        rightSlot={
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#5c534d] mr-1">
            <Flame size={13} className="text-[#ff6a00]" />
            <span data-testid="games-count-header">{games.length.toLocaleString()} games</span>
          </div>
        }
      />

      {/* Hero + Search */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#efe3d6] px-3 py-1 text-xs font-medium text-[#5c534d] mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff6a00]" />
              {loading ? "Loading library" : `${games.length.toLocaleString()} titles`} - zero downloads - member access
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
              Play anything.
              <br />
              <span className="text-[#ff6a00]">Right in your browser.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#5c534d] max-w-2xl">
              A members-only library of browser games. Click a title, it opens
              full-screen. No downloads, no setup, no waiting.
            </p>

            {/* Search */}
            <div className="mt-8 relative max-w-2xl">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5c534d]"
              />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a game or a tag — e.g. 'football', 'zombie', 'io'"
                className="pl-11 pr-11 h-14 text-base bg-white border-[#efe3d6] rounded-2xl shadow-[0_1px_0_0_rgba(0,0,0,0.02)] focus-visible:ring-2 focus-visible:ring-[#ff6a00] focus-visible:ring-offset-0 focus-visible:border-[#ff6a00]"
              />
              {query && (
                <button
                  data-testid="clear-search-btn"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-8 h-8 rounded-full hover:bg-[#ffe4d1] text-[#5c534d] hover:text-[#e85d00] transition-colors"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <p
              className="mt-3 text-sm text-[#5c534d]"
              data-testid="search-results-count"
            >
              {loading
                ? "Loading library…"
                : filtered.length === games.length
                ? `Showing all ${games.length.toLocaleString()} games`
                : `${filtered.length} result${filtered.length === 1 ? "" : "s"} for "${debouncedQuery}"`}
            </p>
          </div>
        </div>
      </section>

      {/* Games Grid */}
      <main className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {loading ? (
          <LoadingGrid />
        ) : filtered.length === 0 ? (
          <EmptyState query={query} onClear={() => setQuery("")} />
        ) : (
          <div
            data-testid="games-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
          >
            {filtered.map((g) => (
              <GameTile
                key={g.slug}
                slug={g.slug}
                name={g.name}
                query={debouncedQuery}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#efe3d6] bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 text-sm text-[#5c534d] flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>
            © {new Date().getFullYear()} Learning{" "}
            <span className="text-[#ff6a00] font-semibold">Games</span>
          </span>
          <span className="text-xs">Built for browsers · Play responsibly.</span>
        </div>
      </footer>
    </div>
  );
}

const LoadingGrid = () => (
  <div
    data-testid="loading-grid"
    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4"
  >
    {Array.from({ length: 15 }).map((_, i) => (
      <div
        key={i}
        className="bg-white border border-[#efe3d6] rounded-2xl p-5 min-h-[112px] animate-pulse"
      >
        <div className="h-2 w-10 bg-[#efe3d6] rounded" />
        <div className="mt-8 h-4 w-3/4 bg-[#efe3d6] rounded" />
        <div className="mt-2 h-4 w-1/2 bg-[#efe3d6] rounded" />
      </div>
    ))}
  </div>
);

const EmptyState = ({ query, onClear }) => (
  <div
    data-testid="empty-state"
    className="mt-6 flex flex-col items-center justify-center text-center py-20 border border-dashed border-[#efe3d6] rounded-3xl bg-white"
  >
    <div className="w-14 h-14 rounded-2xl bg-[#ffe4d1] grid place-items-center text-[#ff6a00]">
      <Search size={22} />
    </div>
    <h3 className="mt-4 font-display text-xl font-bold">No games found</h3>
    <p className="mt-1 text-sm text-[#5c534d] max-w-sm">
      Nothing matched <span className="font-medium text-[#1a1613]">&ldquo;{query}&rdquo;</span>.
      Try another word or clear your search.
    </p>
    <button
      data-testid="empty-clear-btn"
      onClick={onClear}
      className="mt-5 px-5 py-2.5 rounded-full bg-[#ff6a00] text-white text-sm font-semibold hover:bg-[#e85d00] transition-colors"
    >
      Clear search
    </button>
  </div>
);
